"use client";

import React from "react";

// ─── KP Data Interface ───────────────────────────────────────────────────────

interface KpPlanetEntry {
  longitude: number;
  sign: string;
  star_lord: string;
  sub_lord: string;
  sub_sub_lord: string;
}

interface KpCuspEntry {
  cusp_longitude: number;
  sign: string;
  sign_lord: string;
  star_lord: string;
  sub_lord: string;
  sub_sub_lord: string;
}

export interface KpData {
  system: "kp";
  planet_kp: Record<string, KpPlanetEntry>;
  cuspal_kp: Record<string, KpCuspEntry>;
  significators: Record<string, number[]>;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const PLANET_ORDER = [
  "sun",
  "moon",
  "mars",
  "mercury",
  "jupiter",
  "venus",
  "saturn",
  "rahu",
  "ketu",
];

const HOUSE_MATTERS: Record<number, string> = {
  1: "Self / Health",
  2: "Wealth / Family",
  3: "Courage / Siblings",
  4: "Education / Property",
  5: "Children / Intelligence",
  6: "Health / Debts / Enemies",
  7: "Marriage / Partnership",
  8: "Longevity / Transformation",
  9: "Fortune / Father",
  10: "Career / Status",
  11: "Gains / Desires",
  12: "Loss / Abroad / Spirituality",
};

const ORDINALS: Record<number, string> = {
  1: "1st",
  2: "2nd",
  3: "3rd",
  4: "4th",
  5: "5th",
  6: "6th",
  7: "7th",
  8: "8th",
  9: "9th",
  10: "10th",
  11: "11th",
  12: "12th",
};

// ─── Utility Helpers ─────────────────────────────────────────────────────────

function capitalize(s: string): string {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function ordinal(n: number): string {
  return ORDINALS[n] ?? `${n}th`;
}

// ─── Key Reading Derivation ──────────────────────────────────────────────────

interface KeyReading {
  subject: string;
  cslLabel: string;
  cslPlanet: string;
  supportingHouses: number[];
  significantPlanets: string[];
  implication: string;
}

function getPlanetsSignifyingHouses(kp: KpData, houses: number[]): string[] {
  return Object.entries(kp.significators)
    .filter(([, sigs]) => houses.some((h) => sigs.includes(h)))
    .map(([planet]) => capitalize(planet));
}

function getMarriageReading(kp: KpData): KeyReading {
  const houses = [2, 7, 11];
  const cusp = kp.cuspal_kp["7"];
  const cslPlanet = cusp ? capitalize(cusp.sub_lord) : "—";
  const cslSigs = kp.significators[cusp?.sub_lord ?? ""] ?? [];
  const signifiesHouses = houses.filter((h) => cslSigs.includes(h));
  const favourable = signifiesHouses.length >= 2;
  return {
    subject: "Marriage",
    cslLabel: "CSL of 7th House",
    cslPlanet,
    supportingHouses: houses,
    significantPlanets: getPlanetsSignifyingHouses(kp, houses),
    implication: cusp
      ? favourable
        ? `${cslPlanet} sub-lord signifies houses ${signifiesHouses.join(", ")} — favourable for marriage`
        : `${cslPlanet} sub-lord does not strongly signify houses 2, 7, 11 — marriage requires favourable dasha`
      : "Cuspal data unavailable",
  };
}

function getCareerReading(kp: KpData): KeyReading {
  const houses = [2, 6, 10, 11];
  const cusp = kp.cuspal_kp["10"];
  const cslPlanet = cusp ? capitalize(cusp.sub_lord) : "—";
  const cslSigs = kp.significators[cusp?.sub_lord ?? ""] ?? [];
  const signifiesHouses = houses.filter((h) => cslSigs.includes(h));
  const favourable = signifiesHouses.length >= 2;
  return {
    subject: "Career",
    cslLabel: "CSL of 10th House",
    cslPlanet,
    supportingHouses: houses,
    significantPlanets: getPlanetsSignifyingHouses(kp, houses),
    implication: cusp
      ? favourable
        ? `${cslPlanet} sub-lord signifies houses ${signifiesHouses.join(", ")} — strong career promise`
        : `${cslPlanet} sub-lord weakly signifies career houses — progress depends on dasha timing`
      : "Cuspal data unavailable",
  };
}

function getHealthReading(kp: KpData): KeyReading {
  const houses = [1, 6, 8];
  const cusp = kp.cuspal_kp["1"];
  const cslPlanet = cusp ? capitalize(cusp.sub_lord) : "—";
  const cslSigs = kp.significators[cusp?.sub_lord ?? ""] ?? [];
  const signifiesHouses = houses.filter((h) => cslSigs.includes(h));
  const concern = signifiesHouses.includes(6) || signifiesHouses.includes(8);
  return {
    subject: "Health",
    cslLabel: "CSL of 1st House",
    cslPlanet,
    supportingHouses: houses,
    significantPlanets: getPlanetsSignifyingHouses(kp, houses),
    implication: cusp
      ? concern
        ? `${cslPlanet} sub-lord signifies houses ${signifiesHouses.join(", ")} — monitor health during afflicted dashas`
        : `${cslPlanet} sub-lord primarily signifies vitality houses — generally robust constitution`
      : "Cuspal data unavailable",
  };
}

function getWealthReading(kp: KpData): KeyReading {
  const houses = [2, 6, 11];
  const cusp = kp.cuspal_kp["2"];
  const cslPlanet = cusp ? capitalize(cusp.sub_lord) : "—";
  const cslSigs = kp.significators[cusp?.sub_lord ?? ""] ?? [];
  const signifiesHouses = houses.filter((h) => cslSigs.includes(h));
  const favourable = signifiesHouses.length >= 2;
  return {
    subject: "Wealth",
    cslLabel: "CSL of 2nd House",
    cslPlanet,
    supportingHouses: houses,
    significantPlanets: getPlanetsSignifyingHouses(kp, houses),
    implication: cusp
      ? favourable
        ? `${cslPlanet} sub-lord signifies houses ${signifiesHouses.join(", ")} — accumulation of wealth indicated`
        : `${cslPlanet} sub-lord weakly signifies wealth houses — financial gains need strong 11th dasha`
      : "Cuspal data unavailable",
  };
}

// ─── Sub-Components ──────────────────────────────────────────────────────────

function CuspalTable({ kp }: { kp: KpData }) {
  return (
    <div>
      <h3 className="text-[17px] font-bold mb-1 text-ish-dark font-serif">Cuspal Sub-Lords</h3>
      <p className="text-caption mb-4 text-ish-text-faint">
        The Sub-Lord of each house cusp is the decisive factor for whether that house&apos;s matter
        will materialise.
      </p>
      <div className="overflow-x-auto -mx-1">
        <table className="w-full text-caption border-collapse min-w-[560px]">
          <thead>
            <tr className="border-b border-ish-border">
              <th className="text-left py-2 px-2 font-semibold text-ish-text-faint uppercase tracking-wide text-[10px]">
                House
              </th>
              <th className="text-left py-2 px-2 font-semibold text-ish-text-faint uppercase tracking-wide text-[10px]">
                Matter
              </th>
              <th className="text-left py-2 px-2 font-semibold text-ish-text-faint uppercase tracking-wide text-[10px]">
                Sign
              </th>
              <th className="text-left py-2 px-2 font-semibold text-ish-text-faint uppercase tracking-wide text-[10px]">
                Sign Lord
              </th>
              <th className="text-left py-2 px-2 font-semibold text-ish-text-faint uppercase tracking-wide text-[10px]">
                Star Lord
              </th>
              <th className="text-left py-2 px-2 font-semibold text-intent-brand uppercase tracking-wide text-[10px]">
                Sub-Lord ★
              </th>
              <th className="text-left py-2 px-2 font-semibold text-ish-text-faint uppercase tracking-wide text-[10px]">
                SSL
              </th>
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 12 }, (_, i) => i + 1).map((houseNum) => {
              const cusp = kp.cuspal_kp[String(houseNum)];
              const isTrikona = [1, 5, 9].includes(houseNum);
              const isKendra = [1, 4, 7, 10].includes(houseNum);
              // Trikona gets gold tint; kendra (non-trikona) gets blue tint; 1st house is both, use gold
              const rowBg = isTrikona
                ? "bg-[var(--color-gold)]/10"
                : isKendra
                  ? "bg-[var(--color-primary)]/5"
                  : "";

              return (
                <tr
                  key={houseNum}
                  className={`border-b border-ish-border/50 hover:bg-ish-bg transition-colors ${rowBg}`}
                >
                  <td className="py-2 px-2 font-semibold text-ish-dark">
                    {ordinal(houseNum)}
                    {isTrikona && (
                      <span className="ml-1 text-[9px] text-[#b8960c] font-normal">△</span>
                    )}
                    {isKendra && !isTrikona && (
                      <span className="ml-1 text-[9px] text-[var(--color-primary)]/60 font-normal">
                        □
                      </span>
                    )}
                  </td>
                  <td className="py-2 px-2 text-ish-text-muted text-[11px]">
                    {HOUSE_MATTERS[houseNum]}
                  </td>
                  <td className="py-2 px-2 text-ish-text-body">
                    {cusp ? capitalize(cusp.sign) : "—"}
                  </td>
                  <td className="py-2 px-2 text-ish-text-body">
                    {cusp ? capitalize(cusp.sign_lord) : "—"}
                  </td>
                  <td className="py-2 px-2 text-ish-text-body">
                    {cusp ? capitalize(cusp.star_lord) : "—"}
                  </td>
                  <td className="py-2 px-2 font-bold text-intent-brand">
                    {cusp ? capitalize(cusp.sub_lord) : "—"}
                  </td>
                  <td className="py-2 px-2 text-ish-text-muted">
                    {cusp ? capitalize(cusp.sub_sub_lord) : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="flex gap-4 mt-2 text-[10px] text-ish-text-faint">
        <span>
          <span className="text-[#b8960c]">△</span> Trikona (1,5,9)
        </span>
        <span>
          <span className="text-[var(--color-primary)]/60">□</span> Kendra (4,7,10)
        </span>
        <span>
          <span className="text-intent-brand font-bold">★</span> Sub-Lord = decisive factor
        </span>
      </div>
    </div>
  );
}

function PlanetaryTable({ kp }: { kp: KpData }) {
  return (
    <div>
      <h3 className="text-[17px] font-bold mb-1 text-ish-dark font-serif">
        Planetary KP Positions
      </h3>
      <p className="text-caption mb-4 text-ish-text-faint">
        A planet delivers results through its star lord&apos;s house ownership, not its own sign
        placement.
      </p>
      <div className="overflow-x-auto -mx-1">
        <table className="w-full text-caption border-collapse min-w-[520px]">
          <thead>
            <tr className="border-b border-ish-border">
              <th className="text-left py-2 px-2 font-semibold text-ish-text-faint uppercase tracking-wide text-[10px]">
                Planet
              </th>
              <th className="text-left py-2 px-2 font-semibold text-ish-text-faint uppercase tracking-wide text-[10px]">
                Sign
              </th>
              <th className="text-left py-2 px-2 font-semibold text-ish-text-faint uppercase tracking-wide text-[10px]">
                Star Lord
              </th>
              <th className="text-left py-2 px-2 font-semibold text-intent-brand uppercase tracking-wide text-[10px]">
                Sub Lord ★
              </th>
              <th className="text-left py-2 px-2 font-semibold text-ish-text-faint uppercase tracking-wide text-[10px]">
                SSL
              </th>
              <th className="text-left py-2 px-2 font-semibold text-ish-text-faint uppercase tracking-wide text-[10px]">
                Signifies Houses
              </th>
            </tr>
          </thead>
          <tbody>
            {PLANET_ORDER.map((planetId) => {
              const planet = kp.planet_kp[planetId];
              const sigs = kp.significators[planetId] ?? [];
              return (
                <tr
                  key={planetId}
                  className="border-b border-ish-border/50 hover:bg-ish-bg transition-colors"
                >
                  <td className="py-2 px-2 font-semibold text-ish-dark">{capitalize(planetId)}</td>
                  <td className="py-2 px-2 text-ish-text-body">
                    {planet ? capitalize(planet.sign) : "—"}
                  </td>
                  <td className="py-2 px-2 text-ish-text-body">
                    {planet ? capitalize(planet.star_lord) : "—"}
                  </td>
                  <td className="py-2 px-2 font-bold text-intent-brand">
                    {planet ? capitalize(planet.sub_lord) : "—"}
                  </td>
                  <td className="py-2 px-2 text-ish-text-muted">
                    {planet ? capitalize(planet.sub_sub_lord) : "—"}
                  </td>
                  <td className="py-2 px-2 text-ish-text-muted">
                    {sigs.length > 0 ? sigs.join(", ") : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const READING_ICONS: Record<string, string> = {
  Marriage: "♥",
  Career: "★",
  Health: "✦",
  Wealth: "◈",
};

function KeyReadingsSection({ kp }: { kp: KpData }) {
  const readings = [
    getMarriageReading(kp),
    getCareerReading(kp),
    getHealthReading(kp),
    getWealthReading(kp),
  ];

  return (
    <div>
      <h3 className="text-[17px] font-bold mb-1 text-ish-dark font-serif">Key Readings</h3>
      <p className="text-caption mb-4 text-ish-text-faint">
        Each reading derives from the cuspal sub-lord (CSL) test — the KP verdict on whether a
        matter will materialise.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {readings.map((reading) => (
          <div
            key={reading.subject}
            className="rounded-xl border border-ish-border p-4 bg-white/60"
          >
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[18px] text-intent-brand" aria-hidden>
                {READING_ICONS[reading.subject] ?? "◆"}
              </span>
              <h4 className="text-body font-bold text-ish-dark">{reading.subject}</h4>
            </div>
            <p className="text-caption text-ish-text-faint mb-1">{reading.cslLabel}</p>
            <p className="text-body font-bold text-intent-brand mb-2">{reading.cslPlanet}</p>
            <p className="text-caption text-ish-text-muted leading-relaxed mb-3">
              {reading.implication}
            </p>
            <div className="border-t border-ish-border/50 pt-2">
              <p className="text-[10px] text-ish-text-faint uppercase tracking-wide mb-1">
                House Significators ({reading.supportingHouses.join(", ")})
              </p>
              <p className="text-caption text-ish-text-body">
                {reading.significantPlanets.length > 0
                  ? reading.significantPlanets.join(", ")
                  : "None found"}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Main KpResult Component ─────────────────────────────────────────────────

interface KpResultProps {
  kp: KpData;
}

export default function KpResult({ kp }: KpResultProps) {
  return (
    <div className="space-y-8">
      {/* Section A: Cuspal Sub-Lords */}
      <div className="card-sacred p-5 sm:p-7">
        <CuspalTable kp={kp} />
      </div>

      {/* Section B: Planetary KP Table */}
      <div className="card-sacred p-5 sm:p-7">
        <PlanetaryTable kp={kp} />
      </div>

      {/* Section C: Key Readings */}
      <div className="card-sacred p-5 sm:p-7">
        <KeyReadingsSection kp={kp} />
      </div>
    </div>
  );
}
